import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { getMemberAuditLogs, AuditLog } from '../../api/members';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye.svg';
import Modal from '../BaseComponents/Modal';
import './AuditLogs.scss';
import {
  MEMBER_PROFILES_MERGED_ACTION,
  buildMergeProfileDetailRows,
} from '../../utils/auditLogMergeDetails';

type ActionDetailsRecord = Record<string, unknown>;

const TIMEZONE = 'Europe/London';

const parseActionDetails = (raw: unknown): ActionDetailsRecord | null => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as ActionDetailsRecord;
      }
      return { value: parsed };
    } catch {
      return { text: trimmed };
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as ActionDetailsRecord;
  }
  return { value: raw as unknown };
};

const formatFieldLabel = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatRoleLabel = (role: string): string => {
  if (!role) return '—';
  if (role.toLowerCase() === 'client') return 'Member';
  return role
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const formatPerformedAt = (iso: string): string => {
  if (!iso) return '—';
  try {
    return formatInTimeZone(new Date(iso), TIMEZONE, "dd/MM/yyyy 'at' h:mm a");
  } catch {
    return iso;
  }
};

const PRIORITY_DETAIL_KEYS = [
  'recommendation_title',
  'biomarker_names',
  'appointment_id',
  'member_id',
  'note_id',
];

const renderDetailPrimitive = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value || '—';
  return (
    <pre className='audit-log-detail-json'>{JSON.stringify(value, null, 2)}</pre>
  );
};

const ActionDetailsBody: React.FC<{ raw: unknown; actionType?: string }> = ({
  raw,
  actionType,
}) => {
  const details = useMemo(() => parseActionDetails(raw), [raw]);

  const mergeDetails = useMemo(() => {
    if (actionType !== MEMBER_PROFILES_MERGED_ACTION) return null;
    return buildMergeProfileDetailRows(raw);
  }, [actionType, raw]);

  const rows = useMemo(() => {
    if (!details || Object.keys(details).length === 0) {
      return [] as { key: string; label: string; node: React.ReactNode }[];
    }

    const used = new Set<string>();
    const ordered: { key: string; label: string; node: React.ReactNode }[] = [];

    if (actionType === MEMBER_PROFILES_MERGED_ACTION) {
      return ordered;
    }

    const pushKey = (key: string) => {
      if (!Object.prototype.hasOwnProperty.call(details, key) || used.has(key)) return;
      used.add(key);
      const value = details[key];

      if (key === 'biomarker_names') {
        const list = Array.isArray(value)
          ? value.map(String).filter(Boolean)
          : typeof value === 'string'
            ? value.split(',').map((s) => s.trim()).filter(Boolean)
            : [];
        ordered.push({
          key,
          label: 'Biomarker names',
          node: list.length ? list.join(', ') : '—'
        });
        return;
      }

      if (key === 'recommendation_title') {
        ordered.push({
          key,
          label: 'Recommendation title',
          node: renderDetailPrimitive(value)
        });
        return;
      }

      ordered.push({
        key,
        label: formatFieldLabel(key),
        node: renderDetailPrimitive(value)
      });
    };

    PRIORITY_DETAIL_KEYS.forEach(pushKey);

    const restKeys = Object.keys(details)
      .filter((k) => !used.has(k))
      .sort((a, b) => a.localeCompare(b));
    restKeys.forEach(pushKey);

    return ordered;
  }, [details, actionType]);

  if (actionType === MEMBER_PROFILES_MERGED_ACTION) {
    const { profileRows, reassignedTables } =
      mergeDetails ?? { profileRows: [], reassignedTables: null };

    return (
      <div className='audit-log-merge-details'>
        {profileRows.length > 0 ? (
          <div className='audit-log-detail-rows'>
            {profileRows.map((row) => (
              <div key={row.label} className='audit-log-detail-row'>
                <div className='audit-log-detail-label'>{row.label}</div>
                <div className='audit-log-detail-value'>{row.value}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className='audit-log-detail-empty'>No profile details recorded.</p>
        )}
        {reassignedTables && Object.keys(reassignedTables).length > 0 && (
          <div className='audit-log-merge-reassigned'>
            <div className='audit-log-detail-label audit-log-merge-reassigned-title'>
              Reassigned tables
            </div>
            <pre className='audit-log-detail-json'>
              {JSON.stringify(reassignedTables, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  }

  if (rows.length === 0) {
    return <p className='audit-log-detail-empty'>No additional details for this entry.</p>;
  }

  return (
    <div className='audit-log-detail-rows'>
      {rows.map(({ key, label, node }) => (
        <div key={key} className='audit-log-detail-row'>
          <div className='audit-log-detail-label'>{label}</div>
          <div className='audit-log-detail-value'>{node}</div>
        </div>
      ))}
    </div>
  );
};

const AuditLogs: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const limit = 10;

  // Initial fetch
  useEffect(() => {
    const fetchInitialAuditLogs = async () => {
      try {
        setIsLoading(true);
        setAuditLogs([]);
        setHasMore(true);
        
        const memberId = location.pathname.split('/')[2];
        
        if (memberId) {
          console.log('Fetching initial audit logs for member:', memberId);
          const response = await getMemberAuditLogs(memberId, limit, 0);
          console.log('Initial audit logs response:', response);
          setAuditLogs(response.audit_logs || []);
          setTotalCount(response.total_count || 0);
          setHasMore((response.audit_logs || []).length >= limit);
        } else {
          console.log('No member ID found in URL');
          setAuditLogs([]);
          setTotalCount(0);
          setHasMore(false);
        }
      } catch (error) {
        console.error('Error fetching initial audit logs:', error);
        setAuditLogs([]);
        setTotalCount(0);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialAuditLogs();
  }, [location.pathname, limit]);

  // Load more audit logs
  const loadMoreAuditLogs = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    
    try {
      const memberId = location.pathname.split('/')[2];
      if (!memberId) return;
      
      const currentOffset = auditLogs.length;
      console.log('Loading more audit logs, offset:', currentOffset);
      const response = await getMemberAuditLogs(memberId, limit, currentOffset);
      console.log('More audit logs response:', response);
      
      if (response.audit_logs && response.audit_logs.length > 0) {
        setAuditLogs(prev => [...prev, ...response.audit_logs]);
        setHasMore(response.audit_logs.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more audit logs:', error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, limit, location.pathname, auditLogs.length]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const el = scrollContainerRef.current;
      console.log('Scroll event triggered:', { el, loadingMore, hasMore });
      
      if (!el || loadingMore || !hasMore) {
        console.log('Scroll conditions not met:', { el: !!el, loadingMore, hasMore });
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = el;
      const threshold = 100;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
      
      console.log('Scroll position:', { scrollTop, scrollHeight, clientHeight, threshold, isNearBottom });

      if (isNearBottom) {
        console.log('Loading more audit logs...');
        loadMoreAuditLogs();
      }
    }, 150);
  }, [loadMoreAuditLogs, loadingMore, hasMore]);

  // Add scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    console.log('Setting up scroll listener on container:', container);
    
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  const formatDateTime = (dateString: string): { date: string; time: string } => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      
      return {
        date: `${day}/${month}/${year}`,
        time: `${hours}:${minutes}:${seconds}`
      };
    } catch (error) {
      return {
        date: dateString,
        time: ''
      };
    }
  };

  const formatActionType = (actionType: string): string => {
    return actionType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getActionDescription = (log: AuditLog): React.ReactNode => {
    const formattedAction = formatActionType(log.action_type);

    if (formattedAction === 'Ask Your Results Chat') {
      const rawDetails = log.action_details;
      let details: any = rawDetails;

      if (typeof rawDetails === 'string') {
        try {
          details = JSON.parse(rawDetails);
        } catch {
          // keep as string
        }
      }

      const question =
        (typeof details?.question === 'string' && details.question) ||
        (typeof details?.user_question === 'string' && details.user_question) ||
        (typeof details?.prompt === 'string' && details.prompt) ||
        (typeof details?.text === 'string' && details.text) ||
        (typeof details === 'string' && details) ||
        '';

      const trimmedQuestion = question.trim();

      return (
        <div className='audit-action'>
          <div className='audit-action-title'>{formattedAction}</div>
          <div className='audit-action-question'>
            {trimmedQuestion || '—'}
          </div>
        </div>
      );
    }

    return formattedAction;
  };

  if (isLoading) {
    return (
      <div className='search-row-container'>
        <div className='search-and-filter-container sideBarPad2' >
          <div className='count-container'>
            Audit Logs ({totalCount})
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='search-row-container'>
        <div className='search-and-filter-container sideBarPad2'>
          <div className='count-container'>
            Audit Logs ({totalCount})
          </div>
        </div>
      </div>

      <div className='audit-logs-container'>
        <div className='audit-logs-table'>
          <div className='table-header'>
            <div className='header-cell' style={{width: '200px'}} >Date</div>
            <div className='header-cell'>Time</div>
            <div className='header-cell'>Who did it</div>
            <div className='header-cell'>What did they do</div>
            <div className='header-cell header-cell--info'>Details</div>
          </div>
          <div className='table-body' ref={scrollContainerRef}>
            {auditLogs.length > 0 ? (
              <>
                {auditLogs.map((log) => {
                  const { date, time } = formatDateTime(log.performed_at);
                  return (
                    <div key={log.id} className='table-row'>
                      <div className='table-cell' style={{ width: "200px" }}>{date}</div>
                      <div className='table-cell'>{time}</div>
                      <div className='table-cell'>{log.performed_by_name}</div>
                      <div className='table-cell'>{getActionDescription(log)}</div>
                      <div className='table-cell table-cell--info'>
                        <button
                          type='button'
                          className='audit-log-view-btn'
                          aria-label={`View details for audit log ${log.id}`}
                          title='View details'
                          onClick={() => setSelectedLog(log)}
                        >
                          <EyeIcon
                            className='audit-log-view-btn__icon'
                            width={20}
                            height={20}
                            aria-hidden
                          />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {loadingMore && (
                  <div className='loading-more'>
                    <div className='loading-spinner'></div>
                    <div className='loading-text'>Loading more audit logs...</div>
                  </div>
                )}
              </>
            ) : (
              <div className='empty-state'>
                <div className='empty-text'>No audit logs found</div>
                <div className='empty-subtext'>Audit logs will appear here when actions are performed.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title='Audit log details'
        width='min(92vw, 820px)'
      >
        {selectedLog && (
          <div className='audit-log-detail-modal'>
            <section className='audit-log-detail-section'>
              <dl className='audit-log-detail-meta'>
                <div className='audit-log-detail-meta-row'>
                  <dt>Action type</dt>
                  <dd>{formatActionType(selectedLog.action_type)}</dd>
                </div>
                <div className='audit-log-detail-meta-row'>
                  <dt>Performed by</dt>
                  <dd>{selectedLog.performed_by_name || '—'}</dd>
                </div>
                <div className='audit-log-detail-meta-row'>
                  <dt>Role</dt>
                  <dd>{formatRoleLabel(selectedLog.performed_by_role)}</dd>
                </div>
                <div className='audit-log-detail-meta-row'>
                  <dt>Performed at</dt>
                  <dd>{formatPerformedAt(selectedLog.performed_at)}</dd>
                </div>
              </dl>
            </section>

            <section className='audit-log-detail-section audit-log-detail-section--scroll'>
              <h4 className='audit-log-detail-section-title'>Action details</h4>
              <ActionDetailsBody
                raw={selectedLog.action_details}
                actionType={selectedLog.action_type}
              />
            </section>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AuditLogs;
